import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import qrcode from "qrcode-generator";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  useArborInspectionDetail,
  useTreeArborInspections,
  useTreeDetail,
  useTreeEvidence,
  useTreeLotRelation,
  useTreeTimeline,
} from "@/hooks";
import { buildFCADataFromApi } from "@/lib/fca-mapper";
import { estadoFitoConfig, riscoConfig, statusRevisaoConfig } from "@/config/cta-config";
import { inspectionApi, type TreeApi, type TreeEvidenceApi, type TreeLotRelationApi } from "@/lib/api";
import TreeBasePhoto from "@/components/shared/TreeBasePhoto";
import {
  LOT_RELATION_METHOD_LABELS,
  LOT_RELATION_RESPONSIBILITY,
  RESPONSIBILITY_NOTE,
  lotReferenceView,
  lotRelationLabel,
} from "@/config/lot-relation-config";
import { UTM_LABEL } from "@/lib/utm";
import { EVIDENCE_TYPE_LABELS } from "@/config/evidence-type-labels";

/**
 * FCA em formato de documento A4, aberta pelo botao "Gerar PDF" da ficha.
 * Fica fora do layout do sistema (sem menu) e usa a impressao do navegador
 * ("Salvar como PDF"): texto selecionavel e sem biblioteca de PDF a mais.
 * So mostra dado real; o que nao existe aparece como "—", nunca como zero.
 */

const dash = (v: string | null | undefined) => (v && v.trim() && v !== "—" ? v : "—");
const measure = (v: number | null | undefined, unit: string) => (v && v > 0 ? `${v} ${unit}` : "—");
const date = (v: string | null | undefined) => {
  if (!v) return "—";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(v);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : v;
};

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mb-5 break-inside-avoid">
      <h2 className="text-[13px] font-bold uppercase tracking-wide text-[#1B6E4F] border-b border-[#1B6E4F]/40 pb-1 mb-2">
        {title}
      </h2>
      {children}
    </section>
  );
}

function Fields({ rows }: { rows: [string, string][] }) {
  return (
    <dl className="grid grid-cols-1 sm:grid-cols-2 print:grid-cols-2 gap-x-6 gap-y-1.5 text-[12px]">
      {rows.map(([label, value]) => (
        <div key={label} className="flex gap-2">
          <dt className="text-gray-500 shrink-0 w-[120px] sm:w-[150px] print:w-[150px]">{label}</dt>
          <dd className="text-gray-900 font-medium min-w-0 break-words">{value}</dd>
        </div>
      ))}
    </dl>
  );
}

// Espera maxima pelas fotos antes de abrir o dialogo de impressao.
const PHOTO_WAIT_MS = 15000;

/** Foto de evidencia (servida com token): avisa quando terminou, com ou sem sucesso. */
function EvidenceImage({ ev, className, onDone }: { ev: TreeEvidenceApi; className: string; onDone: () => void }) {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const done = useRef(false);
  const finish = useCallback(() => {
    if (!done.current) {
      done.current = true;
      onDone();
    }
  }, [onDone]);
  useEffect(() => {
    let cancelled = false;
    let objectUrl: string | null = null;
    inspectionApi
      .evidenceFileUrl(ev.inspection_id, ev.evidence_id)
      .then((u) => {
        if (cancelled) {
          if (u) URL.revokeObjectURL(u);
          return;
        }
        objectUrl = u;
        if (u) setUrl(u);
        else {
          setFailed(true);
          finish();
        }
      })
      .catch(() => {
        if (!cancelled) {
          setFailed(true);
          finish();
        }
      });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [ev.inspection_id, ev.evidence_id, finish]);
  if (failed) {
    return <div className={`${className} bg-gray-100 flex items-center justify-center text-[10px] text-gray-500`}>Foto indisponível</div>;
  }
  return url ? (
    <img src={url} alt={ev.description ?? "Foto do exemplar"} className={className} onLoad={finish} onError={finish} />
  ) : (
    <div className={`${className} bg-gray-100`} />
  );
}

/** QR Code com o endereco da ficha do exemplar no sistema. */
function FcaQrCode({ url }: { url: string }) {
  const src = useMemo(() => {
    const qr = qrcode(0, "M");
    qr.addData(url);
    qr.make();
    return qr.createDataURL(4, 0);
  }, [url]);
  return <img src={src} alt="QR Code da ficha do exemplar" className="w-[22mm] h-[22mm]" style={{ imageRendering: "pixelated" }} />;
}

function LotRelationSection({ rel }: { rel: TreeLotRelationApi }) {
  const s = rel.suggestion;
  const shown = rel.lot_relation ?? s?.code ?? null;
  const ref = lotReferenceView(s?.reference, rel);
  const official = rel.lot_relation
    ? `${lotRelationLabel(rel.lot_relation)} — ${LOT_RELATION_METHOD_LABELS[rel.method ?? ""] ?? dash(rel.method)} por ${dash(
        rel.confirmed_by_name,
      )} em ${date(rel.confirmed_at)}`
    : "Não confirmado por técnico";
  const rows: [string, string][] = [
    ["Registro oficial", official],
    ["Sugestão do sistema", s ? `${lotRelationLabel(s.code)} — ${s.reason}` : "—"],
    ["Responsável pelo manejo", shown ? LOT_RELATION_RESPONSIBILITY[shown] ?? "A confirmar" : "A confirmar"],
    ["Referência", ref.title ?? "Sem lote de referência"],
    ["Quadra", ref.block],
    ["Lote", ref.lot],
    ["Inscrição imobiliária", ref.registration],
    ["Logradouro de frente", ref.street],
    ["CEP", ref.cepNote && ref.cep === "—" ? ref.cepNote : ref.cep],
  ];
  if (ref.adjacent) rows.splice(6, 0, ["Lote vizinho (divisa)", ref.adjacent]);
  if (rel.notes) rows.push(["Observação", rel.notes]);
  return (
    <Section title="Relação com o lote">
      <Fields rows={rows} />
      <p className="text-[10px] text-gray-500 mt-1.5">{RESPONSIBILITY_NOTE}</p>
    </Section>
  );
}

export default function FCAPrintPage() {
  const { id } = useParams<{ id: string }>();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const numericId = id && /^\d+$/.test(id) ? Number(id) : null;

  const { data: treeRaw, loading: treeLoading, error: treeError } = useTreeDetail(numericId);
  const { inspections, loading: inspLoading } = useTreeArborInspections(numericId);
  const latestValidatedId = useMemo(() => {
    const validated = inspections.filter((i) => i.workflowStatus === "VALIDATED" || i.workflowStatus === "CONCLUDED");
    return validated.length > 0 ? validated[0].id : null;
  }, [inspections]);
  const { inspection: latestDetail, loading: detailLoading } = useArborInspectionDetail(latestValidatedId);
  const { events } = useTreeTimeline(numericId);
  const { evidence, loading: evidenceLoading } = useTreeEvidence(numericId);
  const photos = useMemo(
    () => evidence.filter((e) => e.mime_type?.startsWith("image/") && !e.invalidated_at),
    [evidence],
  );
  // Sem foto-base, a primeira foto de evidencia ocupa o lugar dela.
  const [hasBasePhoto, setHasBasePhoto] = useState<boolean | null>(null);
  const [photosDone, setPhotosDone] = useState(0);
  const onPhotoDone = useCallback(() => setPhotosDone((n) => n + 1), []);
  const { data: lotRel, loading: lotLoading } = useTreeLotRelation(numericId);

  const data = useMemo(
    () => (treeRaw ? buildFCADataFromApi(treeRaw as TreeApi, inspections, latestDetail, events, evidence) : null),
    [treeRaw, inspections, latestDetail, events, evidence],
  );

  const ready =
    !!data && !treeLoading && !inspLoading && !lotLoading && !evidenceLoading && !(latestValidatedId && detailLoading);
  const coverFromEvidence = hasBasePhoto === false && photos.length > 0;
  const expectedPhotos = photos.length + (coverFromEvidence ? 1 : 0);
  const photosReady = hasBasePhoto !== null && photosDone >= expectedPhotos;
  const printed = useRef(false);
  useEffect(() => {
    if (!ready || printed.current || params.get("print") === "0") return;
    document.title = `FCA ${data!.codigoCta}`;
    // As fotos chegam por blob autenticado: imprime quando todas terminaram,
    // ou depois da espera maxima, para nao travar a emissao por uma foto.
    const t = window.setTimeout(() => {
      if (printed.current) return;
      printed.current = true;
      window.print();
    }, photosReady ? 400 : PHOTO_WAIT_MS);
    return () => window.clearTimeout(t);
  }, [ready, data, params, photosReady]);

  if (treeError) {
    return <p className="p-8 text-sm text-red-700">Não foi possível carregar o exemplar: {treeError}</p>;
  }
  if (!ready || !data) {
    return <p className="p-8 text-sm text-gray-500">Preparando a ficha para impressão...</p>;
  }

  const semValidada = data.fitossanidade.semVistoriaValidada;
  const statusRev = statusRevisaoConfig[data.statusRevisao]?.label ?? dash(data.statusRevisao);
  const estado = semValidada ? "Não avaliado" : estadoFitoConfig[data.estadoFitossanitario]?.label ?? dash(data.estadoFitossanitario);
  const risco = semValidada ? "Não avaliado" : riscoConfig[data.classeRisco]?.label ?? dash(data.classeRisco);
  const geradoEm = new Date().toLocaleString("pt-BR");
  const fichaUrl = `${window.location.origin}/cta/${id}`;

  return (
    <div className="bg-gray-100 min-h-screen print:bg-white">
      <style>{`
        @page { size: A4; margin: 14mm 12mm; }
        @media print { .no-print { display: none !important; } body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
      `}</style>

      <div className="no-print sticky top-0 z-10 bg-white border-b border-gray-200 px-4 py-2 flex flex-wrap items-center gap-3">
        <button onClick={() => window.print()} className="btn-primary text-sm">
          <i className="ri-file-pdf-line" aria-hidden="true"></i>
          Imprimir / Salvar como PDF
        </button>
        <span className="text-xs text-gray-500">No diálogo, escolha "Salvar como PDF" como destino.</span>
        <button onClick={() => navigate(`/cta/${id}`)} className="btn-outline text-sm ml-auto">
          <i className="ri-arrow-left-line" aria-hidden="true"></i>
          Voltar à ficha
        </button>
      </div>

      {/* Na tela do celular a folha encolhe; impressa, segue A4. */}
      <article className="mx-auto sm:my-6 print:my-0 bg-white max-w-[210mm] p-4 sm:p-[12mm] print:p-0 shadow print:shadow-none text-gray-900">
        <header className="flex items-start justify-between gap-3 border-b-2 border-[#1B6E4F] pb-3 mb-4">
          <div>
            <p className="text-[11px] uppercase tracking-wider text-gray-500">FloraIQ · Cadastro Técnico Arbóreo</p>
            <h1 className="text-xl font-bold leading-tight">Ficha Cadastral Arbórea (FCA)</h1>
            <p className="text-sm mt-1">
              <span className="font-mono font-semibold">{data.codigoCta}</span>
              {" · "}
              {dash(data.nomePopular)}
              {data.nomeCientifico && data.nomeCientifico !== "—" && (
                <em className="text-gray-600"> ({data.nomeCientifico})</em>
              )}
            </p>
          </div>
          <div className="flex items-start gap-3 shrink-0">
            <div className="text-center">
              <FcaQrCode url={fichaUrl} />
              <p className="text-[8px] text-gray-500 mt-0.5 leading-tight">Ficha online</p>
            </div>
            <img src="/logos/floraiq_logo_oficial.webp" alt="" className="h-9 object-contain hidden sm:block print:block" onError={(e) => (e.currentTarget.style.display = "none")} />
          </div>
        </header>

        <div className="flex flex-col sm:flex-row print:flex-row gap-5 mb-5 break-inside-avoid">
          <div className="w-[52mm] h-[52mm] shrink-0 self-center sm:self-auto print:self-auto border border-gray-200 rounded overflow-hidden">
            {coverFromEvidence ? (
              <EvidenceImage ev={photos[0]} className="w-full h-full object-cover" onDone={onPhotoDone} />
            ) : (
              <TreeBasePhoto treeId={numericId} alt={data.codigoCta} className="w-full h-full object-cover" onStatus={setHasBasePhoto} />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <Fields
              rows={[
                ["Situação cadastral", statusRev],
                ["Tipo", dash(data.tipo)],
                ["Estado fitossanitário", estado],
                ["Classe de risco", risco],
                ["Manejo recomendado", semValidada ? "—" : dash(data.manejoRecomendado)],
                ["Última vistoria validada", semValidada ? "Nenhuma" : date(data.ultimaVistoria)],
                ["Cadastrado em", date(data.dataCadastro)],
                ["Responsável técnico", dash(data.tecnicoResponsavel)],
              ]}
            />
            {semValidada && (
              <p className="mt-3 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
                Nenhuma vistoria validada: a condição atual do exemplar ainda não foi homologada.
              </p>
            )}
          </div>
        </div>

        <Section title="Localização">
          <Fields
            rows={[
              ["Logradouro", dash(data.logradouro)],
              ["Número / referência", dash(data.numero)],
              ["Bairro", dash(data.bairro)],
              ["Município / UF", `${dash(data.municipio)} / ${dash(data.uf)}`],
              ["Latitude", dash(data.latitude)],
              ["Longitude", dash(data.longitude)],
              ["Projeto", dash(data.projetoVinculado)],
              ["Sistema de referência", dash(data.srid)],
              ["UTM E (leste)", data.utmE === "—" ? "—" : `${data.utmE} m`],
              ["UTM N (norte)", data.utmN === "—" ? "—" : `${data.utmN} m`],
              ["Projeção UTM", data.utmE === "—" ? "—" : UTM_LABEL],
            ]}
          />
        </Section>

        {lotRel && <LotRelationSection rel={lotRel} />}

        <Section title="Dendrometria (última vistoria validada)">
          <Fields
            rows={[
              ["DAP", semValidada ? "—" : measure(data.dap, "cm")],
              ["Altura", semValidada ? "—" : measure(data.altura, "m")],
              ["Diâmetro de copa", semValidada ? "—" : measure(data.diametroCopa, "m")],
              ["Fotos registradas", String(data.fotosCount)],
            ]}
          />
        </Section>

        {!semValidada && (
          <Section title="Fitossanidade e risco">
            <Fields
              rows={[
                ["Classe F", dash(data.fitossanidade.classeF)],
                ["Classe R", dash(data.fitossanidade.classeR)],
                ["Classe A", dash(data.fitossanidade.classeA)],
                ["Copa", dash(data.fitossanidade.condicaoCopa)],
                ["Tronco", dash(data.fitossanidade.condicaoTronco)],
                ["Raízes", dash(data.fitossanidade.condicaoRaizes)],
              ]}
            />
            {data.fitossanidade.parecerTecnico && data.fitossanidade.parecerTecnico !== "—" && (
              <p className="text-[12px] mt-2"><span className="text-gray-500">Parecer técnico: </span>{data.fitossanidade.parecerTecnico}</p>
            )}
          </Section>
        )}

        <section className="mb-5">
          <h2 className="text-[13px] font-bold uppercase tracking-wide text-[#1B6E4F] border-b border-[#1B6E4F]/40 pb-1 mb-2">
            Registro fotográfico ({photos.length})
          </h2>
          {photos.length === 0 ? (
            <p className="text-[12px] text-gray-500">Nenhuma foto de evidência registrada.</p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 print:grid-cols-3 gap-3">
              {photos.map((ev) => (
                <figure key={ev.evidence_id} className="break-inside-avoid">
                  <EvidenceImage ev={ev} className="w-full aspect-square object-cover rounded border border-gray-200" onDone={onPhotoDone} />
                  <figcaption className="text-[9px] text-gray-600 mt-0.5 leading-tight">
                    {EVIDENCE_TYPE_LABELS[ev.evidence_type ?? ""] ?? "Foto"} · {date(ev.captured_at ?? ev.uploaded_at)}
                    {ev.uploaded_by_name ? ` · ${ev.uploaded_by_name}` : ""}
                  </figcaption>
                </figure>
              ))}
            </div>
          )}
        </section>

        <Section title={`Vistorias (${data.vistorias.length})`}>
          {data.vistorias.length === 0 ? (
            <p className="text-[12px] text-gray-500">Nenhuma vistoria registrada.</p>
          ) : (
            <div className="overflow-x-auto print:overflow-visible">
              <table className="w-full text-[11px] border-collapse">
                <thead>
                  <tr className="text-left text-gray-500 border-b border-gray-300">
                    <th className="py-1 pr-2">Data</th><th className="pr-2">Tipo</th><th className="pr-2">Técnico</th>
                    <th className="pr-2">DAP</th><th className="pr-2">Altura</th><th className="pr-2">Risco</th><th>Situação</th>
                  </tr>
                </thead>
                <tbody>
                  {data.vistorias.map((v) => (
                    <tr key={v.id} className="border-b border-gray-100">
                      <td className="py-1 pr-2">{date(v.data)}</td>
                      <td className="pr-2">{dash(v.tipo)}</td>
                      <td className="pr-2">{dash(v.tecnico)}</td>
                      <td className="pr-2">{measure(v.dap, "cm")}</td>
                      <td className="pr-2">{measure(v.altura, "m")}</td>
                      <td className="pr-2">{riscoConfig[v.risco]?.label ?? dash(v.risco)}</td>
                      <td>{dash(v.status)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Section>

        <Section title="Histórico do exemplar">
          {data.timeline.length === 0 ? (
            <p className="text-[12px] text-gray-500">Sem eventos registrados.</p>
          ) : (
            <ul className="text-[11px] space-y-1">
              {data.timeline.map((e) => (
                <li key={e.id} className="flex gap-3">
                  <span className="text-gray-500 w-[70px] shrink-0">{date(e.data)}</span>
                  <span>
                    <span className="font-semibold">{e.titulo}</span>
                    {e.descricao && <span className="text-gray-700"> — {e.descricao}</span>}
                    {e.tecnico && e.tecnico !== "—" && <span className="text-gray-500"> ({e.tecnico})</span>}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Section>

        <footer className="mt-6 pt-2 border-t border-gray-300 text-[10px] text-gray-500 flex justify-between">
          <span>Documento gerado pelo FloraIQ em {geradoEm}. Dados conforme o cadastro no momento da emissão.</span>
          <span className="font-mono">{data.codigoCta}</span>
        </footer>
      </article>
    </div>
  );
}
